import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AddContactModal from 'components/add-contact-modal/add-contact-modal';
import { DeleteConfirmationModal } from 'components/delete-confirmation';
import React, { FC, useCallback, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import useAlert from 'utils/alert';
import { TFunction } from 'i18next';

import { getValidKinds } from '../../../services';
import { AddMortgageAssociation, DeleteMortgageAssociation } from '../../../services/mutations';
import { OrderMortgageType } from '../../../services/types.d';
import TitleSection from './title-section';
import { TextButton } from 'components/TextButton';

const COARSE_POINTER = '@media (hover: none) and (pointer: coarse)';

const useStyles = makeStyles((theme: any) => ({
  partyCard: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: { padding: theme.spacing(1.5) },
  },
  partyInfo: {
    flex: 1,
    minWidth: 0,
  },
  partyName: {
    fontWeight: 500,
    overflowWrap: 'anywhere',
  },
  partyActions: {
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(0.5),
  },
  deleteButton: {
    padding: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
      width: theme.spacing(5.5),
      height: theme.spacing(5.5),
    },
    [COARSE_POINTER]: {
      width: theme.spacing(5.5),
      height: theme.spacing(5.5),
    },
  },
  emptyState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
  addButton: {
    [COARSE_POINTER]: {
      '& .MuiButton-root': { minHeight: theme.spacing(5.5) },
    },
  },
}));

type PendingPartyDelete = {
  partyId: string;
  name: string;
  kind: string;
};

type partiesMortgageType = {
  currentMortgage: OrderMortgageType | undefined;
  t: TFunction;
};

const PartiesMortgage: FC<partiesMortgageType> = ({ currentMortgage, t }) => {
  const [openAddModal, setOpenAddModal] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PendingPartyDelete | null>(null);
  const classes = useStyles();
  const theme = useTheme();
  const queryCache = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const showAlert = useAlert();
  const { mutate: addMortgageAssociation, ...addMortgageAssociacionResponse } = useMutation(
    AddMortgageAssociation,
    {
      onSuccess: () => {
        queryCache.invalidateQueries(['order-mortgages', id]);
        showAlert(t('payoffs:success-party-added'), 'success');
        setOpenAddModal(false);
      },
    }
  );

  const { mutate: deleteMortgageAssociation, ...deleteMortgageAssociacionResponse } = useMutation(
    DeleteMortgageAssociation,
    {
      onSuccess: () => {
        showAlert(t('payoffs:success-party-deleted'), 'success');
        setPendingDelete(null);
        queryCache.invalidateQueries(['order-mortgages', id]);
      },
    }
  );

  const AddMortgageAssociatioFunc = useCallback(
    (kind: string, partyId: string) => {
      addMortgageAssociation({
        id,
        mortgageId: currentMortgage?._id || '',
        kind,
        partyId,
      });
    },
    [addMortgageAssociation, currentMortgage?._id, id]
  );

  const DeleteMortgageF = useCallback(
    (kind: string, partyId: string) => {
      deleteMortgageAssociation({
        id,
        mortgageId: currentMortgage?._id || '',
        kind,
        partyId,
      });
    },
    [currentMortgage?._id, deleteMortgageAssociation, id]
  );

  const isDeletingParty = deleteMortgageAssociacionResponse.isLoading;

  const addButton = (
    <Box className={classes.addButton}>
      <TextButton
        icon="plus"
        text={t('payoffs:add-new-party')}
        textColor={theme.palette.secondary.main}
        onClick={() => {
          setOpenAddModal(true);
        }}
      />
    </Box>
  );

  return (
    <TitleSection title={t('orders:parties')} actionButton={addButton}>
      <>
        {currentMortgage?.parties && currentMortgage.parties.length > 0 ? (
          currentMortgage.parties.map((party) => (
            <Box className={classes.partyCard} key={`${party._id}-${party.kinds?.join(',')}`}>
              <Box className={classes.partyInfo}>
                <Typography variant="body1" className={classes.partyName}>
                  {party.name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {party.kinds.join(', ')}
                </Typography>
              </Box>
              <Box className={classes.partyActions}>
                {getValidKinds(party.kinds.join(',')).map((k) => {
                  const deleteKindLabel = `${t('payoffs:delete-party-kind', { kind: k })}`;
                  return (
                    <Tooltip key={`${party._id}${k}`} title={deleteKindLabel}>
                      <span>
                        <IconButton
                          className={classes.deleteButton}
                          aria-label={deleteKindLabel}
                          disabled={isDeletingParty}
                          onClick={() => {
                            setPendingDelete({ partyId: party._id, name: party.name, kind: k });
                          }}
                          size="large"
                        >
                          <FontAwesomeIcon icon="trash-alt" size="xs" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          ))
        ) : (
          <Box className={classes.emptyState}>
            <Typography variant="body2" color="textSecondary">
              {t('payoffs:no-parties-added')}
            </Typography>
          </Box>
        )}
        <DeleteConfirmationModal
          isOpen={!!pendingDelete}
          title={t('payoffs:delete-party')}
          confirmationMessage={t('payoffs:delete-party-confirmation', {
            name: pendingDelete?.name || '',
            kind: pendingDelete?.kind || '',
          })}
          onClose={() => setPendingDelete(null)}
          onConfirm={() => {
            if (pendingDelete) {
              DeleteMortgageF(pendingDelete.kind, pendingDelete.partyId);
            }
          }}
          isRemoveLoading={isDeletingParty}
          buttonConfirmText={t('common:delete')}
        />
        {openAddModal && (
          <AddContactModal
            open={openAddModal}
            onClose={() => {
              setOpenAddModal(false);
            }}
            allowedCodeAssociations="allowed_mortgage_associations"
            onClickAddParty={(partyId: string, kind: string) => {
              AddMortgageAssociatioFunc(kind, partyId);
            }}
            loading={addMortgageAssociacionResponse.isLoading}
          />
        )}
      </>
    </TitleSection>
  );
};

export default PartiesMortgage;
